import React from 'react';
import { Prompt, useHistory, useLocation } from 'react-router';
import { Location, Action } from 'history';
import { useResettableState } from 'utils';
import { UnsavedChangesWarningModal } from './UnsavedChangesWarningModal';
import { SavingInProgressModal } from './SavingInProgressModal';

export interface RouterPromptProps {
  when?: boolean | undefined;
  /**
   * If it returns `true` or `undefined` the modal will close and the navigation will be performed.
   * If it returns `false` the modal will close and the navigation will be canceled.
   * If it `throws an error` the modal won't close and the navigation will be canceled.
   * @returns
   */
  onConfirm: () => void | boolean | Promise<void | boolean>;
  /**
   * While a save is in progress there is nothing more to save, so a blocked
   * navigation asks only whether to leave before that save finishes.
   */
  isSaving?: boolean;
}

type NavigationInfo = {
  location: Location;
  action: Action;
};

export function RouterPrompt(props: RouterPromptProps): React.ReactElement {
  const { when, onConfirm, isSaving = false } = props;

  const history = useHistory();
  const currentLocation = useLocation();

  const [navigationInfo, setNavigationInfo] =
    React.useState<NavigationInfo | null>(null);
  const [confirmedNavigation, setConfirmedNavigation] = React.useState(false);
  // The question the open prompt asks, decided when the navigation is blocked,
  // so a save finishing while it is open doesn't swap it for the other one.
  const {
    value: prompt,
    setValue: openPrompt,
    resetValue: closePrompt,
  } = useResettableState<'unsaved' | 'saving' | null>(null, null);
  // Counts the prompts opened and closed, so a save confirmed from one that
  // has since closed or given way to another one has no say any more.
  const promptIdRef = React.useRef(0);
  const endPrompt = (): void => {
    promptIdRef.current += 1;
    closePrompt();
  };

  const handlePromptBeforeNavigation = (
    location: Location,
    action: Action,
  ): boolean => {
    const locationChanged =
      currentLocation.pathname !== location.pathname ||
      currentLocation.search !== location.search;
    if (locationChanged && !confirmedNavigation) {
      promptIdRef.current += 1;
      openPrompt(isSaving ? 'saving' : 'unsaved');
      setNavigationInfo({ location, action });
      return false;
    }
    return true;
  };

  const onSaveChangesHandler = async (): Promise<void> => {
    const promptId = promptIdRef.current;
    const result = await onConfirm?.();
    // Closed, or left another way, while the save was running.
    if (promptId !== promptIdRef.current) return;
    endPrompt();
    if (result === undefined || result === true) {
      setConfirmedNavigation(true);
    }
  };

  const onDiscardChangesHandler = (): void => {
    endPrompt();
    setConfirmedNavigation(true);
  };

  const navigate = React.useCallback(
    ({ location, action }: NavigationInfo): void => {
      switch (action) {
        case 'PUSH':
          history.push(location);
          break;
        case 'POP':
          history.goBack();
          break;
        case 'REPLACE':
          history.replace(location);
          break;
        default:
          break;
      }
    },
    [history],
  );

  React.useEffect(() => {
    if (confirmedNavigation && navigationInfo) {
      // Navigate to the previously blocked location
      navigate(navigationInfo);
      // Nothing is left for a later confirmation to navigate to again.
      setNavigationInfo(null);
    }
  }, [confirmedNavigation, navigationInfo]);

  // reset confirmedNavigation when the location changes
  React.useEffect(() => {
    if (confirmedNavigation) {
      setConfirmedNavigation(false);
    }
  }, [currentLocation.pathname, currentLocation.search]);

  return (
    <>
      <Prompt when={when} message={handlePromptBeforeNavigation} />
      <UnsavedChangesWarningModal
        open={prompt === 'unsaved'}
        isSaving={isSaving}
        onSaveChanges={onSaveChangesHandler}
        onDiscardChanges={onDiscardChangesHandler}
        onCancel={endPrompt}
      />
      <SavingInProgressModal
        open={prompt === 'saving'}
        onLeave={onDiscardChangesHandler}
        onStay={endPrompt}
      />
    </>
  );
}
